import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CalendarIcon, Loader2 } from 'lucide-react';
import { format, parseISO } from 'date-fns';

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { useToast } from '@/hooks/use-toast';
import { sprintService, type SprintDetail } from '@/services/sprint.service';
import { cn } from '@/lib/utils';

interface EditSprintDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sprint: SprintDetail;
}

export function EditSprintDialog({ open, onOpenChange, sprint }: EditSprintDialogProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [name, setName] = useState(sprint.name);
  const [description, setDescription] = useState(sprint.description || '');
  const [startDate, setStartDate] = useState<Date | undefined>(parseISO(sprint.start_date));
  const [endDate, setEndDate] = useState<Date | undefined>(parseISO(sprint.end_date));

  useEffect(() => {
    if (open) {
      setName(sprint.name);
      setDescription(sprint.description || '');
      setStartDate(parseISO(sprint.start_date));
      setEndDate(parseISO(sprint.end_date));
    }
  }, [open, sprint]);

  const updateMut = useMutation({
    mutationFn: () =>
      sprintService.update(sprint.id, {
        name,
        description: description || undefined,
        start_date: startDate ? format(startDate, 'yyyy-MM-dd') : undefined,
        end_date: endDate ? format(endDate, 'yyyy-MM-dd') : undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sprint', sprint.id] });
      queryClient.invalidateQueries({ queryKey: ['work-sprints'] });
      toast({ title: 'Sprint updated' });
      onOpenChange(false);
    },
    onError: (e: any) => toast({ title: 'Error', description: e.response?.data?.error?.message || 'Failed', variant: 'destructive' }),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit Sprint</DialogTitle>
        </DialogHeader>

        <form onSubmit={(e) => { e.preventDefault(); updateMut.mutate(); }} className="space-y-4">
          <div className="space-y-2">
            <Label>Sprint Name *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Sprint name" required />
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <textarea
              className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-y"
              placeholder="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={cn('w-full pl-3 text-left font-normal', !startDate && 'text-muted-foreground')}>
                    {startDate ? format(startDate, 'PPP') : 'Pick a date'}
                    <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={startDate} onSelect={setStartDate} initialFocus />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label>End Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={cn('w-full pl-3 text-left font-normal', !endDate && 'text-muted-foreground')}>
                    {endDate ? format(endDate, 'PPP') : 'Pick a date'}
                    <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={endDate} onSelect={setEndDate} initialFocus />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" className="bg-brand-primary hover:bg-brand-primary-dark" disabled={updateMut.isPending}>
              {updateMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
